<i18n>
{
  "fr": {
    "message-snackbar": "Vous devez sélectionner un ou des voyageurs,<br>votre itinéraire et vos dates de voyage.",
    "popup-title": "Relancer une recherche",
    "popup-text": "Souhaitez-vous conserver les mêmes filtres que ceux actuellement sélectionnés pour cette nouvelle recherche ?",
    "popup-btn-with-filters": "Filtres identiques",
    "popup-btn-without-filters": "Pas de filtres"
  },
  "en": {
    "message-snackbar": "You must select a traveler(s), your itinerary and travel dates.",
    "popup-title": "Relaunch a search",
    "popup-text": "Do you want to keep the same filters as those currently selected for this new search?",
    "popup-btn-with-filters": "Same filters",
    "popup-btn-without-filters": "No filters"
  }
}
</i18n>

<template>
  <v-card
    flat
  >
    <v-form>
      <v-row
        no-gutters
        justify="start"
        align-content="start"
        align="start"
        class="pt-3"
      >
        <v-col
          cols="3"
          class="ml-4 pb-0"
        >
          <FlightSelectTravellers
            class="body-2"
          />
        </v-col>
        <v-col
          cols="3"
          class="pb-0"
        >
          <p />
        </v-col>
        <v-col
          cols="2"
          class="pb-0"
        >
          <FlightSelectTravelType
            class="body-2"
          />
        </v-col>
        <v-col
          cols="2"
          class="pb-0"
        >
          <FlightSelectClassType
            class="body-2"
          />
        </v-col>
      </v-row>
      <v-row
        no-gutters
        justify="start"
        align-content="start"
        align="start"
      >
        <v-col
          cols="3"
          class="ml-4 mr-1"
        >
          <FlightAutoCompleteAirport
            :is-departure="true"
            background-color="grey lighten-2"
          />
        </v-col>
        <div class="mx-1">
          <v-btn
            id="swap-button"
            data-cy="flight-research-bar-swap-button"
            color="primaryLight"
            depressed
            tile
            fab
            rounded
            width="48"
            height="48"
            @click="swapDestination"
          >
            <v-icon
              :class="{'swapped': isSwapped}"
              color="primary"
              size="25"
            >
              mdi-swap-horizontal
            </v-icon>
          </v-btn>
        </div>
        <v-col
          cols="3"
          class="mx-1"
        >
          <FlightAutoCompleteAirport
            :is-departure="false"
            background-color="grey lighten-2"
          />
        </v-col>
        <v-col
          cols="4"
          class="mx-1"
        >
          <FlightDatePicker
            background-color="grey lighten-2"
          />
        </v-col>
        <v-col class="ml-1 mr-4">
          <v-btn
            data-cy="flight-research-bar-submit-button"
            class="d-flex mx-auto"
            color="primary"
            depressed
            block
            width="48"
            height="48"
            :disabled="isLoading"
            :loading="isLoading"
            @click="clickSubmitForm"
          >
            <v-icon>
              mdi-magnify
            </v-icon>
          </v-btn>
        </v-col>
      </v-row>
    </v-form>
    <transition name="opacity">
      <Advertisements
        v-if="showAdvertisements"
        class="mt-0 pt-0"
      />
    </transition>
    <v-snackbar
      id="error-form-complete"
      v-model="snackbarVisible"
      color="error"
      top
      timeout="10000"
    >
      <p
        id="error-form-complete-text"
        v-html="$i18n.t('message-snackbar')"
      />
      <template #action>
        <v-btn
          icon
          @click="closeSnackbar"
        >
          <v-icon color="white">
            mdi-close
          </v-icon>
        </v-btn>
      </template>
    </v-snackbar>

    <v-dialog
      v-model="dialog"
      persistent
      max-width="390"
    >
      <v-card>
        <v-toolbar
          flat
          dark
          color="primary"
        >
          <v-toolbar-title>{{ $i18n.t('popup-title') }}</v-toolbar-title>
          <v-spacer />
          <v-btn
            icon
            dark
            @click="dialog = false"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>
        <v-card-text class="mt-3">
          {{ $i18n.t('popup-text') }}
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            color="green-darken-1"
            variant="text"
            @click="clickSubmitFormDialog(true)"
          >
            {{ $i18n.t('popup-btn-with-filters') }}
          </v-btn>
          <v-btn
            color="green-darken-1"
            variant="text"
            @click="clickSubmitFormDialog(false)"
          >
            {{ $i18n.t('popup-btn-without-filters') }}
          </v-btn>
          <v-spacer />
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex'
import FlightSelectTravellers from './FlightSelectTravellers/FlightSelectTravellers'
import FlightSelectTravelType from './FlightSelectTravelType/FlightSelectTravelType'
import FlightSelectClassType from './FlightSelectClassType/FlightSelectClassType'
import FlightAutoCompleteAirport from './FlightAutoCompleteAirport/FlightAutoCompleteAirport'
import FlightDatePicker from './FlightDatePicker/FlightDatePicker'
import Advertisements from '../../General/Advertisements/Advertisements'

export default {
  name: 'FlightResearchBar',
  components: {
    FlightSelectTravellers,
    FlightSelectTravelType,
    FlightSelectClassType,
    FlightAutoCompleteAirport,
    FlightDatePicker,
    Advertisements
  },
  data: () => ({
    isSwapped: false,
    snackbarVisible: false,
    dialog: false
  }),
  computed: {
    ...mapState({
      isLoading: state => state.fb.searchResults.loading,
      selectedAirlineCompaniesState: state => state.fb.searchFilters.selectedAirlineCompanies,
      step: state => state.fb.step,
      selectedStopoverState: state => state.fb.searchFilters.selectedStopover,
      outwardFlightDepartureDeltaState: state => state.fb.searchFilters.outwardFlightDepartureDelta,
      returnFlightDepartureDeltaState: state => state.fb.searchFilters.returnFlightDepartureDelta
    }),
    ...mapGetters({
      canSubmit: 'fb/searchParameters/parametersValidity',
      airlineCompanies: 'fb/searchFilters/getAirlineCompanies'
    }),
    /**
     * Used to display advertisements during step 1 of the process
     * @return {boolean}
     */
    showAdvertisements () {
      return this.step === '1'
    }
  },
  methods: {
    ...mapActions({
      submitForm: 'fb/flightSearchAction',
      swapAirports: 'fb/searchParameters/swapAirports'
    }),
    ...mapMutations({
      changeSelectedAirlineCompanies: 'fb/searchFilters/setSelectedAirlineCompanies',
      changeOutwardFlightDepartureTimeDelta: 'fb/searchFilters/setOutwardFlightDepartureDelta',
      changeReturnFlightDepartureTimeDelta: 'fb/searchFilters/setReturnFlightDepartureDelta',
      changeSelectedStopover: 'fb/searchFilters/setSelectedStopover'
    }),
    /**
     * Used to change the class of the swap button for the css animation
     * Also call to a vueX action
     */
    swapDestination () {
      this.isSwapped = !this.isSwapped
      this.swapAirports()
    },
    closeSnackbar () {
      this.snackbarVisible = false
    },

    isDefaultFlightDepartureValue () {
      return !(this.outwardFlightDepartureDeltaState.begin !== 0 ||
        this.outwardFlightDepartureDeltaState.end !== 24 ||
        this.returnFlightDepartureDeltaState.begin !== 0 ||
        this.returnFlightDepartureDeltaState.end !== 24)
    },
    isDefaultAirlineValue () {
      return this.selectedAirlineCompaniesState.length === this.airlineCompanies.length
    },
    isDefaultStopoverValue () {
      return this.selectedStopoverState === 'manystopover'
    },

    resetAllFilters () {
      const newDelta = {
        begin: 0,
        end: 24
      }
      this.changeOutwardFlightDepartureTimeDelta(newDelta)
      this.changeReturnFlightDepartureTimeDelta(newDelta)
      this.changeSelectedStopover('manystopover')
      this.changeSelectedAirlineCompanies([])
    },
    clickSubmitForm () {
      if (this.canSubmit) {
        this.snackbarVisible = false
        // console.log('clickSubmitForm step', _.cloneDeep(this.step))
        if (String(this.step) === '1') {
          // console.log('clickSubmitForm step 1 OK', _.cloneDeep(this.step))
          this.submitForm()
        } else {
          if (this.isDefaultFlightDepartureValue() && this.isDefaultAirlineValue() && this.isDefaultStopoverValue()) {
            // console.log('clickSubmitForm step DEFAULT VALUES')
            this.resetAllFilters()
            this.submitForm()
          } else {
            this.dialog = true
          }
        }
      } else {
        this.snackbarVisible = true
      }
    },
    clickSubmitFormDialog (withFilter) {
      if (withFilter) {
        if (this.isDefaultAirlineValue()) {
          this.changeSelectedAirlineCompanies([])
        }
      } else {
        this.resetAllFilters()
      }
      // console.log('clickSubmitFormDialog', _.cloneDeep(this.step), _.cloneDeep(this.selectedAirlineCompaniesState), (this.selectedAirlineCompaniesState || []).length)
      this.dialog = false
      this.submitForm()
    }
  }
}
</script>

<style scoped>
#swap-button {
  border-radius: 4px;
  transition: ease-in-out 200ms;
}

#swap-button::before {
  background-color: transparent;
}

.swapped {
  transform: rotate(180deg);
}

.opacity-enter-active, .opacity-leave-active {
  transition: opacity 0.4s;
}

.opacity-enter, .opacity-leave-to {
  opacity: 0;
}

#error-form-complete-text {
  color: white !important;
  padding-left: 12px;
  margin-bottom: 0 !important;
  font-weight: bold;
}
#error-form-complete {
  margin-top: 107px;
}

</style>
